import React from "react";
import { Check, Building2, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SleekButton from "@/components/ui/NavButtons";
import { WealthScopeGlyph } from "@/pages/platform/appCustomIcons";

type PricingSectionProps = {
  billingPeriod: "monthly" | "annual";
};

const APP_URL = "https://app.vawena.ai";

function PriceBlock({
  billingPeriod,
  monthly,
  annual,
  annualMonthlyEquiv,
  annualSavingsAmt,
  highlight,
}: {
  billingPeriod: "monthly" | "annual";
  monthly: number;
  annual: number;
  annualMonthlyEquiv: number;
  annualSavingsAmt: number;
  highlight: boolean;
}) {
  const isAnnual = billingPeriod === "annual";
  const mainTextClass = highlight ? "text-white" : "dark:text-white text-gray-900";
  const subTextClass = highlight ? "text-blue-200" : "dark:text-slate-400 text-gray-500";

  return (
    <div className="mt-0.5">
      <div className="relative h-[44px]">
        <div
          className={`absolute inset-0 flex items-end gap-1 transition-all duration-300 ease-out ${
            isAnnual ? "opacity-0 -translate-y-1 pointer-events-none" : "opacity-100 translate-y-0"
          }`}
        >
          <span className={`text-4xl font-bold tabular-nums ${mainTextClass}`}>${monthly}</span>
          <span className={`text-sm mb-1 ${subTextClass}`}>/month</span>
        </div>
        <div
          className={`absolute inset-0 flex items-end gap-1 transition-all duration-300 ease-out ${
            isAnnual ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1 pointer-events-none"
          }`}
        >
          <span className={`text-4xl font-bold tabular-nums ${mainTextClass}`}>${annual}</span>
          <span className={`text-sm mb-1 ${subTextClass}`}>/year</span>
        </div>
      </div>

      <div className="relative mt-0.5 h-[38px]">
        <div
          className={`absolute inset-0 transition-all duration-300 ease-out ${
            isAnnual ? "opacity-0 -translate-y-1 pointer-events-none" : "opacity-100 translate-y-0"
          }`}
        >
          <p className={`text-xs ${subTextClass} leading-tight`}>&nbsp;</p>
          <p className={`text-xs ${subTextClass} leading-tight`}>&nbsp;</p>
        </div>
        <div
          className={`absolute inset-0 transition-all duration-300 ease-out ${
            isAnnual ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1 pointer-events-none"
          }`}
        >
          <div className="flex flex-col leading-tight">
            <p className={`text-xs ${subTextClass}`}>${annualMonthlyEquiv}/mo billed annually</p>
            <p className="text-xs text-emerald-400 mt-1 font-semibold">Save ${annualSavingsAmt}/year (17% off)</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Pricing must match backend source of truth: vawena-backend/shared/payments/core/entitlements.py
// Annual = 2 months free (10 months billed), ~17% off
// WealthScope: $9.99/mo → $99.90/yr | PortfolioIQ: $29.99/mo → $299.90/yr | Bundle: $34.99/mo → $349.90/yr
const PLANS = [
  {
    key: "wealthscope",
    icon: WealthScopeGlyph,
    iconBg: "bg-blue-500/20",
    iconColor: "dark:text-blue-300 text-blue-600",
    badge: null,
    name: "WealthScope",
    tagline: "Know when you're ready to invest",
    monthly: 9.99,
    annual: 99.90,
    annualMonthlyEquiv: 8.33,
    annualSavingsAmt: 19.98,
    cta: "Start Free Trial",
    ctaStyle: "",
    highlight: false,
    features: [
      "Net worth dashboard & tracking",
      "Buying power & investment capacity",
      "Debt-to-income analysis",
      "Monthly cash flow breakdown",
      "Bank account sync (Plaid)",
      "Investment milestone tracker",
      "Deal readiness scoring",
      "AI-powered financial insights",
      "Automated alerts & notifications",
      "Financial reports (PDF)",
    ],
  },
  {
    key: "bundle",
    icon: Layers,
    iconBg: "bg-white/20",
    iconColor: "text-white",
    badge: "BEST VALUE · SAVE 17%",
    name: "VaWeNa Bundle",
    tagline: "Complete real estate investor OS",
    monthly: 34.99,
    annual: 349.90,
    annualMonthlyEquiv: 29.16,
    annualSavingsAmt: 69.98,
    cta: "Start Free Trial",
    ctaStyle: "bg-white text-[#1D2B5A] hover:bg-blue-50 font-bold",
    highlight: true,
    features: [
      "Everything in WealthScope",
      "Everything in PortfolioIQ",
      "Priority support",
      "Early access to new features",
      "Unlimited bank connections",
      "Unlimited property analysis",
      "Schedule E tax reports",
      "Lender-ready DSCR reports",
      "What-if refinance analysis",
      "AI portfolio optimization",
    ],
  },
  {
    key: "portfolioiq",
    icon: Building2,
    iconBg: "bg-emerald-500/20",
    iconColor: "dark:text-emerald-300 text-emerald-600",
    badge: null,
    name: "PortfolioIQ",
    tagline: "Manage & grow your properties",
    monthly: 29.99,
    annual: 299.90,
    annualMonthlyEquiv: 24.99,
    annualSavingsAmt: 59.98,
    cta: "Start Free Trial",
    ctaStyle: "",
    highlight: false,
    features: [
      "Unlimited property tracking",
      "Cap rate & cash-on-cash returns",
      "DSCR analysis & lender reports",
      "Property performance dashboards",
      "What-if refinance analysis",
      "Schedule E tax filing reports",
      "Portfolio-level analytics",
      "AI underperformance alerts",
      "Market value benchmarking",
      "Document management",
    ],
  },
];

export const PricingSection: React.FC<PricingSectionProps> = ({ billingPeriod }) => {
  const navigate = useNavigate();
  const isAnnual = billingPeriod === "annual";

  const handleCTA = (_plan: (typeof PLANS)[0]) => {
    window.location.href = `${APP_URL}/signup`;
  };

  return (
    <section className="px-4 sm:px-6 pb-14 mx-auto max-w-[1200px]">
      {/* Trial banner */}
      <div className="text-center mb-8">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 dark:text-emerald-300 text-emerald-700 text-sm font-semibold">
          <Check className="w-4 h-4" />
          14-day free trial · We'll remind you before you're charged
        </span>
      </div>

      {/* Annual savings hint */}
      {isAnnual && (
        <div className="text-center mb-6">
          <p className="dark:text-slate-300 text-gray-600 text-sm">
            💰 Save up to <span className="dark:text-white text-gray-900 font-bold">$69.98/year</span> with annual billing — <span className="dark:text-emerald-400 text-emerald-600 font-semibold">17% off</span>
          </p>
        </div>
      )}

      {/* Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {PLANS.map((plan) => {
          const Icon = plan.icon;

          return (
            <article
              key={plan.key}
              className={`relative rounded-3xl overflow-hidden border transition-all duration-300 hover:shadow-2xl flex flex-col ${
                plan.highlight
                  ? "z-10 scale-[1.03] lg:scale-[1.05] hover:scale-[1.06] border-[#7CA6FF]/60 dark:hover:border-blue-300/70 dark:bg-gradient-to-b dark:from-vw-highlight dark:to-vw-bg bg-gradient-to-b from-blue-600 to-blue-800 shadow-[0_0_40px_rgba(124,166,255,0.15)]"
                  : "hover:scale-[1.02] dark:border-white/10 dark:hover:border-blue-300/60 border-blue-300 dark:bg-vw-surface/60 bg-white dark:hover:shadow-[0_0_30px_rgba(96,165,250,0.12)]"
              }`}
            >
              {/* Best value badge */}
              {plan.badge && (
                <div className="absolute top-0 left-0 right-0 text-center py-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-[11px] font-bold tracking-widest">
                  {plan.badge}
                </div>
              )}

              {/* Header */}
              <div className={`px-5 border-b dark:border-white/10 border-gray-200 ${plan.badge ? "pt-9 pb-4" : "pt-5 pb-4"}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl ${plan.iconBg} flex items-center justify-center shrink-0 mt-0.5`}>
                    <Icon className={`w-5 h-5 ${plan.iconColor}`} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-xl font-bold dark:text-white text-gray-900 mb-1 leading-tight">{plan.name}</h3>
                    <p className={`text-sm ${plan.highlight ? "text-blue-100" : "dark:text-slate-400 text-gray-500"}`}>{plan.tagline}</p>
                  </div>
                </div>

                <PriceBlock
                  billingPeriod={billingPeriod}
                  monthly={plan.monthly}
                  annual={plan.annual}
                  annualMonthlyEquiv={plan.annualMonthlyEquiv}
                  annualSavingsAmt={plan.annualSavingsAmt}
                  highlight={plan.highlight}
                />
              </div>

              {/* Features */}
              <div className="px-5 py-5 flex-1">
                <p className={`text-[11px] font-semibold uppercase tracking-widest mb-3 ${plan.highlight ? "text-blue-200" : "dark:text-slate-500 text-gray-900"}`}>
                  What's included
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5">
                      <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                      <span className={`text-[13px] leading-snug ${plan.highlight ? "text-blue-50" : "dark:text-slate-300 text-gray-900"}`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA (footer) */}
              <div className="px-5 pb-5">
                <SleekButton
                  type="button"
                  onClick={() => handleCTA(plan)}
                  variant={plan.highlight ? "primary" : "light"}
                  className="!w-full !h-11 !min-w-0 !px-5 !py-2.5 !text-sm !font-semibold !rounded-full justify-center"
                >
                  {plan.cta}
                </SleekButton>
              </div>
            </article>
          );
        })}
      </div>

      {/* Enterprise row */}
      <div className="mt-8 rounded-2xl border dark:border-white/10 dark:hover:border-blue-300/60 border-blue-300 dark:bg-vw-surface/40 bg-white px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4 transition-all duration-300 dark:hover:shadow-[0_0_24px_rgba(96,165,250,0.12)]">
        <div>
          <p className="dark:text-white text-gray-900 font-semibold">Enterprise / Teams</p>
          <p className="dark:text-slate-400 text-gray-500 text-sm">
            Custom pricing · Multi-user access · API integrations · Dedicated support
          </p>
        </div>
        <SleekButton
          type="button"
          onClick={() => navigate("/contact")}
          variant="light"
          className="shrink-0 !min-w-[180px] !h-11 !px-6 !py-2.5 !text-sm !font-semibold !rounded-full justify-center"
        >
          Contact Sales →
        </SleekButton>
      </div>
    </section>
  );
};
